/**
 * SRES · Project Details — simplified single-page form
 * ---------------------------------------------------
 * Feb 2026 · Frontend-only navigation-simplification pass.
 *
 * Replaces the previous 12-card Project Setup Command Center behind
 * the same route `/app/projects/:pid/setup`. The client mandate:
 *   • ONE screen, four grouped sections, no advanced parameters, no
 *     AI explanations, no readiness checklist, no publication gate.
 *   • ONE primary action button — Save & Continue → next stage
 *     (Borehole Data & Boundary Upload).
 *   • Values are editable at any time; auto-save is debounced.
 *
 * Backend contract UNCHANGED:
 *   • GET   /api/projects/:pid/setup-dashboard   (aggregator)
 *   • PATCH /api/projects/:pid/setup-dashboard   (existing whitelist)
 * No fields are added to the backend whitelist in this pass. Fields
 * that the backend does not yet accept (client contact info, report
 * type / number) are persisted client-side via localStorage so the
 * user sees them survive a refresh; backend extension can wire them
 * in a later sprint without any UI change.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import ArchiveTimeline from "./ArchiveTimeline";
import { CheckCircle, CloudArrowUp, ArrowRight, ArrowLeft, House, Compass } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const AUTO_SAVE_DEBOUNCE_MS = 2500;

// ── Static option lists (kept in sync with backend enums; adding a
//    new option here is a purely presentational change) ────────────
const REPORT_TYPES = [
  ["CPR",              "Competent Person's Report"],
  ["MRE",              "Mineral Resource Estimate"],
  ["EXPLORATION",      "Exploration Report"],
  ["INTERNAL",         "Internal Technical Report"],
  ["DUE_DILIGENCE",    "Technical Due Diligence"],
  ["SCOPING",          "Scoping Study"],
  ["PEA",              "Preliminary Economic Assessment"],
  ["PFS",              "Pre-Feasibility Study"],
  ["DFS",              "Definitive Feasibility Study"],
  ["BFS",              "Bankable Feasibility Study"],
  ["MINE_PLAN",        "Mine Planning Report"],
  ["RESOURCE_RESERVE", "Resource & Reserve Report"],
];
const REPORTING_STANDARDS = [
  ["JORC_2012",     "JORC 2012"],
  ["NI_43_101",     "NI 43-101"],
  ["SEC_S_K_1300",  "SEC S-K 1300"],
  ["SAMREC",        "SAMREC"],
  ["CRIRSCO",       "CRIRSCO"],
  ["PERC",          "PERC"],
  ["UNFC",          "UNFC"],
];
const COMMODITIES = [
  ["AU", "Gold (Au)"], ["AG", "Silver (Ag)"], ["CU", "Copper (Cu)"],
  ["FE", "Iron Ore (Fe)"], ["NI", "Nickel (Ni)"], ["ZN", "Zinc (Zn)"],
  ["PB", "Lead (Pb)"], ["LI", "Lithium (Li)"], ["CO", "Cobalt (Co)"],
  ["U",  "Uranium (U)"], ["PT", "Platinum (Pt)"], ["PD", "Palladium (Pd)"],
  ["CR", "Chromium (Cr)"], ["MN", "Manganese (Mn)"], ["SN", "Tin (Sn)"],
  ["MO", "Molybdenum (Mo)"], ["W",  "Tungsten (W)"], ["GRAPHITE", "Graphite"],
  ["REE", "Rare Earth Elements"], ["COAL", "Coal"], ["GEMS", "Gemstones"],
];
const GRADE_UNITS = [
  ["g/t",  "g/t (grams per tonne)"],
  ["%",    "% (percent)"],
  ["ppm",  "ppm (parts per million)"],
  ["oz/t", "oz/t (ounces per tonne)"],
  ["kg/t", "kg/t (kilograms per tonne)"],
  ["carats/t", "carats/t"],
];
const CURRENCIES = [
  ["USD","USD (US Dollar)"], ["EUR","EUR (Euro)"], ["AUD","AUD (Australian Dollar)"],
  ["CAD","CAD (Canadian Dollar)"], ["GBP","GBP (British Pound)"],
  ["ZAR","ZAR (S. African Rand)"], ["INR","INR (Indian Rupee)"],
  ["TZS","TZS (Tanzanian Shilling)"], ["KES","KES (Kenyan Shilling)"],
  ["JPY","JPY (Japanese Yen)"], ["CNY","CNY (Chinese Yuan)"],
  ["BRL","BRL (Brazilian Real)"], ["CLP","CLP (Chilean Peso)"],
];

// Fields the backend now persists via PATCH /setup-dashboard (Feb 2026
// whitelist extension). Every Project Details field round-trips.
const BACKEND_PERSISTED = new Set([
  "name", "project_code", "client", "consultant",
  "commodity", "grade_unit", "reporting_currency", "reporting_standard",
  "report_type", "report_number",
  "client_address", "client_email", "client_contact",
  "consultant_address", "consultant_email", "consultant_contact",
  "datum", "utm_zone",
  "country", "epsg", "coordinate_system",
]);

// Fields kept in `localStorage` as an ADDITIONAL fallback while the
// backend catches up on any cross-device sync corner-cases. Empty for
// now — all 18 fields persist server-side.
const LOCAL_ONLY = new Set();

// ── Derive Datum + UTM Zone from CRS/EPSG (client-side helpers) ────
function utmZoneFromEpsg(epsg) {
  const n = Number(epsg);
  if (!Number.isFinite(n)) return "";
  if (n >= 32601 && n <= 32660) return `${n - 32600}N`;
  if (n >= 32701 && n <= 32760) return `${n - 32700}S`;
  return "";
}
function datumFromCrs(crs, verticalDatum) {
  if (verticalDatum) return verticalDatum;
  if (!crs) return "";
  const s = String(crs).toUpperCase();
  if (s.includes("WGS")) return "WGS 84";
  if (s.includes("ARC 1960") || s.includes("ARC1960")) return "Arc 1960";
  if (s.includes("ARC 1950")) return "Arc 1950";
  if (s.includes("NAD83"))    return "NAD83";
  if (s.includes("GDA2020"))  return "GDA2020";
  if (s.includes("SIRGAS"))   return "SIRGAS 2000";
  return "";
}

// Local storage helpers for fields the backend does not yet persist.
const LS_KEY = (pid) => `sres.project-details.local.${pid}`;
function loadLocalOnly(pid) {
  try { return JSON.parse(localStorage.getItem(LS_KEY(pid)) || "{}") || {}; }
  catch { return {}; }
}
function saveLocalOnly(pid, patch) {
  try {
    const cur = loadLocalOnly(pid);
    localStorage.setItem(LS_KEY(pid), JSON.stringify({ ...cur, ...patch }));
  } catch { /* quota */ }
}

// Auto-generate a stable, unique report number if one isn't set yet.
function autoReportNumber(pid, projectCode) {
  const code = (projectCode || "SRES").replace(/\s+/g, "").toUpperCase();
  const yy   = new Date().getUTCFullYear();
  // last 6 chars of pid for uniqueness (idempotent)
  const suffix = String(pid || "").replace(/[^A-Za-z0-9]/g, "").slice(-6).toUpperCase() || "000000";
  return `${code}-${yy}-${suffix}`;
}

// ═══════════════════════════════════════════════════════════════════
// The page
// ═══════════════════════════════════════════════════════════════════
export default function ProjectSetupCommandCenter() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [search] = useSearchParams();
  // Iter86 · Feb 2026 · Chip Action deep-link. When landing from a
  // rose/amber CRS chip on the Projects page (?focus=country) we
  // scroll to the Technical section and pop the country dropdown open
  // so the user can pick a country with a single click.
  useEffect(() => {
    if (search.get("focus") !== "country") return;
    const t = setTimeout(() => {
      try {
        const trig = document.querySelector('[data-testid="pd-field-country"]');
        if (trig) {
          trig.scrollIntoView({ behavior: "smooth", block: "center" });
          trig.focus();
          trig.click();          // opens the Radix Select popover
        }
      } catch { /* silent */ }
    }, 900);   // allow the setup dashboard query to hydrate first
    return () => clearTimeout(t);
  }, [search]);
  const qc  = useQueryClient();

  const dashQ = useQuery({
    queryKey: ["setup-dashboard", pid],
    queryFn:  () => api.get(`/projects/${pid}/setup-dashboard`).then((r) => r.data),
    enabled:  !!pid,
    // Iter98e · Never retry on 404/410 — the project genuinely does
    // not exist and we're about to redirect. Cap other errors at 1
    // retry so transient network hiccups still recover.
    retry: (failureCount, err) => {
      const s = err?.response?.status;
      if (s === 404 || s === 410) return false;
      return failureCount < 1;
    },
    refetchInterval: (q) => {
      const s = q?.state?.error?.response?.status;
      return (s === 404 || s === 410) ? false : 60_000;
    },
  });
  const data = dashQ.data;

  // Fetch the CRS-country library (43 countries · Iter81 · Feb 2026).
  // Small stable payload → cache for 30 minutes.
  const countriesQ = useQuery({
    queryKey: ["crs-countries"],
    queryFn:  () => api.get(`/gis/crs/countries`).then((r) => r.data),
    staleTime: 30 * 60_000,
  });
  const countryItems = useMemo(() => countriesQ.data?.items || [], [countriesQ.data]);
  // Map ISO alpha-2 codes ("TZ") → canonical dropdown names ("Tanzania")
  const countryByCode = useMemo(() => {
    const m = {};
    for (const c of countryItems) {
      if (c.country_code) m[c.country_code.toUpperCase()] = c.name;
    }
    return m;
  }, [countryItems]);
  const normalizeCountry = useCallback((raw) => {
    if (!raw) return "";
    const s = String(raw).trim();
    if (s.length === 2 && countryByCode[s.toUpperCase()]) {
      return countryByCode[s.toUpperCase()];
    }
    return s;
  }, [countryByCode]);

  // Merge backend project + client-side local-only fields.
  const local = useMemo(() => (pid ? loadLocalOnly(pid) : {}), [pid]);
  const p     = useMemo(() => data?.project || {}, [data]);
  const crs   = useMemo(() => data?.crs || {}, [data]);
  const form = useMemo(() => {
    const rn = local.report_number
      || p.report_number
      || autoReportNumber(pid, p.project_code);
    return {
      // Section A — Project Identity
      name:               p.name || "",
      project_code:       p.project_code || "",
      report_type:        local.report_type || p.report_type || "CPR",
      reporting_standard: p.reporting_standard || "JORC_2012",
      report_number:      rn,
      // Section B — Client / Owner
      client:              p.client || "",
      client_address:      local.client_address ?? p.client_address ?? "",
      client_email:        local.client_email   ?? p.client_email   ?? "",
      client_contact:      local.client_contact ?? p.client_contact ?? "",
      // Section C — Consultant / Prepared by
      consultant:              p.consultant || "",
      consultant_address:      local.consultant_address ?? p.consultant_address ?? "",
      consultant_email:        local.consultant_email   ?? p.consultant_email   ?? "",
      consultant_contact:      local.consultant_contact ?? p.consultant_contact ?? "",
      // Section D — Technical
      commodity:          p.commodity || "AU",
      grade_unit:         p.grade_unit || "g/t",
      reporting_currency: p.reporting_currency || "USD",
      country:            normalizeCountry(p.country || (p.location || {}).country),
      datum:              local.datum ?? p.datum ?? p.vertical_datum
                                   ?? datumFromCrs(p.crs || crs.crs, p.vertical_datum),
      utm_zone:           local.utm_zone
                                   ?? p.utm_zone
                                   ?? utmZoneFromEpsg(p.epsg ?? crs.epsg) ?? "",
      epsg:               p.epsg ?? crs.epsg ?? "",
      coordinate_system:  p.coordinate_system ?? "",
      // Iter93 · Feb 2026 · surface archive audit fields so the
      // read-only banner can render on archived projects.
      archived:        !!p.archived,
      archived_at:     p.archived_at || null,
      archived_by:     p.archived_by || null,
      archive_reason:  p.archive_reason || null,
      archive_note:    p.archive_note || null,
    };
  }, [p, crs, local, pid, normalizeCountry]);

  // ── Auto-save plumbing ───────────────────────────────────────────
  const [dirty, setDirty] = useState({});
  const [saveState, setSaveState] = useState("saved");
  const [savedAt, setSavedAt]     = useState(null);
  const timerRef  = useRef(null);
  const inFlight  = useRef(false);
  // Iter98f · Cross-project auto-save recovery. When a previous
  // session's project went missing mid-edit, we cached the unsaved
  // patch to localStorage. Surface it here (once) so the user can
  // restore it into the current project or discard it.
  const [recoveryOffer, setRecoveryOffer] = useState(null);
  const [recoveryOfferOpen, setRecoveryOfferOpen] = useState(false);
  // Iter98i · 30-second undo window for silent auto-restores.
  // Iter98k · Timer pauses while the mouse is over the chip so the
  // user isn't rushed to react. On mouseleave the remaining time
  // resumes.
  const [autoRestoreUndo, setAutoRestoreUndo] = useState(null);
  const [undoHovered, setUndoHovered] = useState(false);
  const undoTimerRef = useRef(null);
  useEffect(() => {
    if (!autoRestoreUndo) return;
    // Clear any prior scheduled expiry when hover state or chip changes.
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    if (undoHovered) return; // paused — do not schedule expiry
    const ms = Math.max(0, autoRestoreUndo.expires_at - Date.now());
    undoTimerRef.current = setTimeout(() => setAutoRestoreUndo(null), ms);
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    };
  }, [autoRestoreUndo, undoHovered]);
  useEffect(() => {
    if (!pid) return;
    try {
      const raw = window.localStorage.getItem("sres.recovery.orphan");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.project_id === pid) {
        // Same project came back — auto-discard.
        window.localStorage.removeItem("sres.recovery.orphan");
        return;
      }
      if (parsed?.patch && Object.keys(parsed.patch).length > 0) {
        // Iter98h · Auto-restore for tiny stashes (≤3 fields) — skip
        // the banner entirely and quietly apply, with a toast that
        // lists exactly which fields were restored.
        const keys = Object.keys(parsed.patch);
        if (keys.length <= 3) {
          // Iter98i · Snapshot previous values BEFORE overwriting so
          // the auto-restore is undoable for 30 s.
          const previous = Object.fromEntries(keys.map((k) => [k, form[k]]));
          for (const k of keys) patchField(k, parsed.patch[k]);
          window.localStorage.removeItem("sres.recovery.orphan");
          const preview = keys.map((k) => `${k} = ${String(parsed.patch[k] ?? "")}`).join(" · ");
          toast.success(
            `Auto-restored ${keys.length} field${keys.length === 1 ? "" : "s"}: ${preview}`,
            { duration: 8_000 },
          );
          setAutoRestoreUndo({
            keys, previous,
            expires_at: Date.now() + 30_000,
          });
          return;
        }
        setRecoveryOffer(parsed);
      }
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: fire once per project mount; adding form/patchField would re-run the recovery banner on every field edit
  }, [pid]);

  const patchMut = useMutation({
    mutationFn: (patch) => api.patch(`/projects/${pid}/setup-dashboard`, patch).then((r) => r.data),
    onError: (err) => {
      // Iter98e · Guard against auto-save 404 storms. If the project
      // has been deleted (or archived by an admin) mid-session, the
      // PATCH will 404 forever — kill the debounce timer, stop future
      // saves and bounce the user back to the projects list with a
      // clear message. Any other error is surfaced normally.
      const status = err?.response?.status;
      if (status === 404) {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        setSaveState("gone");
        // Iter98f · Cache the still-dirty patch to a global slot so
        // the next Setup mount can offer a one-click restore. Users
        // never lose the typing they had in flight when a project is
        // deleted out from under them.
        try {
          const stash = { ...dirty };
          if (Object.keys(stash).length > 0) {
            window.localStorage.setItem(
              "sres.recovery.orphan",
              JSON.stringify({
                project_id: pid,
                project_name: form.name || null,
                patch: stash,
                stashed_at: new Date().toISOString(),
              }),
            );
          }
        } catch { /* silent — storage disabled */ }
        setDirty({});
        inFlight.current = true;
        toast.error("This project no longer exists — redirecting to the projects list.", {
          duration: 6_000,
        });
        setTimeout(() => nav("/app/projects", { replace: true }), 900);
        return;
      }
      setSaveState("error");
      toast.error(err?.response?.data?.detail?.message
                     || err?.response?.data?.message
                     || "Auto-save failed");
    },
    onSuccess: (resp) => {
      setSavedAt(resp?.updated_at || new Date().toISOString());
      setSaveState("saved");
      setDirty({});
    },
  });

  const flush = useCallback((extraPatch) => {
    const changes = { ...dirty, ...(extraPatch || {}) };
    const backendPatch = {};
    const localPatch   = {};
    for (const [k, v] of Object.entries(changes)) {
      if (BACKEND_PERSISTED.has(k)) backendPatch[k] = v;
      if (LOCAL_ONLY.has(k))         localPatch[k]   = v;
    }
    if (Object.keys(localPatch).length > 0 && pid) saveLocalOnly(pid, localPatch);
    if (Object.keys(backendPatch).length === 0) {
      // Nothing to send — still clear dirty state.
      setSaveState("saved");
      setDirty({});
      return Promise.resolve();
    }
    if (inFlight.current) return Promise.resolve();
    inFlight.current = true;
    setSaveState("saving");
    return patchMut.mutateAsync(backendPatch)
      .finally(() => { inFlight.current = false; });
  }, [dirty, patchMut, pid]);

  const patchField = useCallback((field, value) => {
    setDirty((d) => ({ ...d, [field]: value }));
    setSaveState("dirty");
    // Mirror to the cached query so the UI stays snappy.
    if (BACKEND_PERSISTED.has(field)) {
      qc.setQueryData(["setup-dashboard", pid], (prev) =>
        prev ? { ...prev, project: { ...prev.project, [field]: value } } : prev);
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(), AUTO_SAVE_DEBOUNCE_MS);
  }, [flush, qc, pid]);

  // ── Country → auto-fill Datum + UTM + EPSG ───────────────────────
  //
  // Feb 2026 · Iter81 · P0 requirement: when the user picks a
  // country, call /api/gis/crs/country-defaults and fill Datum,
  // UTM Zone, EPSG, and Coordinate System. Fields remain editable.
  // If the user has already edited a field, we show a confirmation
  // before overwriting.
  const [autoFillMeta, setAutoFillMeta] = useState(null);  // banner state
  const [pendingCountry, setPendingCountry] = useState(null); // for confirm
  const applyCountryDefaults = useCallback(async (countryName, forceOverwrite = false) => {
    if (!countryName) return;
    try {
      const { data: def } = await api.get(`/gis/crs/country-defaults`, {
        params: { country: countryName },
      });
      // Fields that would be overwritten
      const overwrites = [];
      const patch = {};
      const cur = form;
      const maybe = (field, newVal, prevVal) => {
        if (newVal == null || newVal === "") return;
        const changed = String(prevVal ?? "").trim() !== String(newVal).trim();
        if (!changed) return;
        if (prevVal && !forceOverwrite) {
          overwrites.push({ field, prev: prevVal, next: newVal });
        } else {
          patch[field] = newVal;
        }
      };
      maybe("datum",              def.preferred_datum,    cur.datum);
      maybe("utm_zone",            def.utm_zone,            cur.utm_zone);
      maybe("epsg",                def.epsg,                cur.epsg);
      maybe("coordinate_system",   def.coordinate_system,   cur.coordinate_system);
      Object.entries(patch).forEach(([k, v]) => patchField(k, v));

      // Banner metadata
      const isMultiZone = !!(
        (def.utm_north && def.utm_north.length > 1) ||
        (def.utm_south && def.utm_south.length > 1)
      );
      setAutoFillMeta({
        country:             def.country || countryName,
        datum:                def.preferred_datum,
        utm_zone:             def.utm_zone,
        epsg:                 def.epsg,
        coordinate_system:    def.coordinate_system,
        confidence:           def.confidence,
        multi_zone_hint:      isMultiZone,
        applied_at:           new Date().toISOString(),
      });

      if (overwrites.length > 0) {
        // User has manual edits — surface confirmation instead of silent overwrite
        setPendingCountry({ country: countryName, overwrites });
      } else {
        setPendingCountry(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail
                       || "Failed to fetch country defaults");
    }
  }, [form, patchField]);

  const handleCountryChange = useCallback((countryName) => {
    patchField("country", countryName);
    applyCountryDefaults(countryName, false);
  }, [patchField, applyCountryDefaults]);

  const confirmOverwrite = useCallback(() => {
    if (!pendingCountry) return;
    applyCountryDefaults(pendingCountry.country, true);
    setPendingCountry(null);
  }, [pendingCountry, applyCountryDefaults]);
  const cancelOverwrite = useCallback(() => setPendingCountry(null), []);

  // On initial project load, if country is set but datum/UTM/EPSG are
  // missing, auto-populate them from the country library. This runs at
  // most once per pid so we don't loop on user edits.
  const autoFillRunRef = useRef(false);
  useEffect(() => {
    if (autoFillRunRef.current) return;
    if (!pid || dashQ.isLoading) return;
    const country = form.country;
    if (!country) return;
    const missing = !form.datum || !form.utm_zone || !form.epsg;
    if (!missing) return;
    autoFillRunRef.current = true;
    applyCountryDefaults(country, false);
  }, [pid, dashQ.isLoading, form.country, form.datum,
       form.utm_zone, form.epsg, applyCountryDefaults]);

  // Warn the user if they navigate away with unsaved changes.
  useEffect(() => {
    const handler = (ev) => {
      if (saveState === "dirty" || saveState === "saving") {
        ev.preventDefault(); ev.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [saveState]);

  // Persist the auto-generated report number once so it survives refresh.
  // Since Feb 2026, `report_number` is on the backend whitelist too — if
  // the project has none, we auto-generate + PATCH it in the background.
  useEffect(() => {
    if (!pid) return;
    if (!p.report_number && form.report_number) {
      patchMut.mutate({ report_number: form.report_number });
    }
  }, [pid, p.report_number, form.report_number, patchMut]);

  // ── Save & Continue ──────────────────────────────────────────────
  const handleSaveContinue = useCallback(async () => {
    try { await flush(); } catch { /* swallowed — user sees toast */ }
    nav(`/app/projects/${pid}/wizard/data_upload`);
  }, [flush, nav, pid]);

  const handleBack = useCallback(() => {
    nav(`/app/projects/new`);
  }, [nav]);

  const handleReturnDashboard = useCallback(() => {
    nav(`/app/dashboard`);
  }, [nav]);

  const handleSave = useCallback(async () => {
    try { await flush(); toast.success("Project details saved"); }
    catch { /* toast handled in mutation */ }
  }, [flush]);

  // ── Loading / error ──────────────────────────────────────────────
  // Iter98e · When the project truly does not exist (HTTP 404), auto-
  // bounce the user back to the portfolio dashboard after a short
  // grace window. We intentionally do NOT mount the editor in this
  // case so no auto-save timer can fire against a phantom project.
  const dashStatus = dashQ.error?.response?.status;
  const projectGone = dashStatus === 404 || dashStatus === 410;
  useEffect(() => {
    if (!projectGone) return;
    toast.error("Project not found — returning to the projects list.", { duration: 5_000 });
    const t = setTimeout(() => nav("/app/projects", { replace: true }), 1_400);
    return () => clearTimeout(t);
  }, [projectGone, nav]);

  if (dashQ.isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50" data-testid="pd-loading">
        <div className="text-slate-500 text-sm">Loading project details…</div>
      </div>
    );
  }
  if (projectGone) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50 p-6" data-testid="pd-not-found">
        <div className="max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
          <div className="text-amber-800 font-semibold text-sm">Project not found</div>
          <div className="mt-1 text-[12px] text-amber-700">
            It may have been deleted or archived. Redirecting to the projects list…
          </div>
        </div>
      </div>
    );
  }
  if (dashQ.error || !data?.project) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50 p-6" data-testid="pd-error">
        <div className="max-w-md rounded-lg border border-rose-200 bg-rose-50 p-6">
          <div className="text-rose-800 font-semibold text-sm">Project Details failed to load.</div>
          <div className="mt-1 text-[12px] text-rose-700">
            {dashQ.error?.response?.data?.detail?.message
              || dashQ.error?.message
              || "The project record is empty."}
          </div>
          <button
            type="button"
            className="mt-3 text-[12px] font-medium text-rose-800 underline hover:text-rose-900"
            onClick={() => nav("/app/projects")}
            data-testid="pd-error-back">
            Back to projects
          </button>
        </div>
      </div>
    );
  }

  const saveLabel = saveState === "saved"  ? "All changes saved"
                   : saveState === "saving" ? "Saving…"
                   : saveState === "error"  ? "Save failed"
                                            : "Unsaved changes";

  return (
    <div
      className="h-full min-h-0 flex flex-col bg-slate-50 text-slate-900"
      data-testid="project-details-page"
    >
      {/* Iter98i · Auto-restore undo chip — visible for 30 s after a
          silent auto-restore so the user can bail if it wasn't wanted. */}
      {autoRestoreUndo && (
        <div className="shrink-0 bg-emerald-50 border-b border-emerald-200 px-6 py-1.5 flex items-center gap-3 text-[12px]"
                 data-testid="pd-auto-restore-undo"
                 onMouseEnter={() => {
                   setAutoRestoreUndo((prev) => prev
                     ? { ...prev, remaining_ms: Math.max(0, prev.expires_at - Date.now()) }
                     : prev);
                   setUndoHovered(true);
                 }}
                 onMouseLeave={() => {
                   // Extend expires_at by the paused duration so the
                   // countdown resumes from where it left off.
                   setAutoRestoreUndo((prev) => prev
                     ? { ...prev, expires_at: Date.now() + (prev.remaining_ms ?? 0) }
                     : prev);
                   setUndoHovered(false);
                 }}
                 onFocusCapture={() => {
                   setAutoRestoreUndo((prev) => prev
                     ? { ...prev, remaining_ms: Math.max(0, prev.expires_at - Date.now()) }
                     : prev);
                   setUndoHovered(true);
                 }}
                 onBlurCapture={(e) => {
                   // Only unpause when focus leaves the whole chip.
                   if (!e.currentTarget.contains(e.relatedTarget)) {
                     setAutoRestoreUndo((prev) => prev
                       ? { ...prev, expires_at: Date.now() + (prev.remaining_ms ?? 0) }
                       : prev);
                     setUndoHovered(false);
                   }
                 }}>
          <span className="text-emerald-800">
            Auto-restored <b>{autoRestoreUndo.keys.length}</b> field{autoRestoreUndo.keys.length === 1 ? "" : "s"}.
          </span>
          {/* Iter98j · Countdown ring — visible time until the chip
              auto-dismisses. Uses a stroke-dashoffset animation so
              there is zero JS ticking during the 30 s.
              Iter98k · Ring animation freezes while the chip is hovered
              (or keyboard-focused) — matches the paused expiry timer. */}
          <div className="relative w-5 h-5 shrink-0" aria-hidden="true">
            <svg viewBox="0 0 20 20" className="w-5 h-5 -rotate-90">
              <circle cx="10" cy="10" r="8" fill="none"
                      stroke="rgba(16,185,129,0.2)" strokeWidth="2" />
              <circle cx="10" cy="10" r="8" fill="none"
                      stroke="rgb(16,185,129)" strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray="50.265" strokeDashoffset="0"
                      data-testid="pd-auto-restore-undo-ring"
                      style={{
                        animation: "pd-undo-countdown 30s linear forwards",
                        animationPlayState: undoHovered ? "paused" : "running",
                      }} />
            </svg>
          </div>
          {undoHovered && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700"
                     data-testid="pd-auto-restore-undo-paused">
              paused
            </span>
          )}
          <button
            type="button"
            className="ml-auto text-[11.5px] font-semibold text-emerald-800 underline hover:text-emerald-950"
            onClick={() => {
              for (const k of autoRestoreUndo.keys) patchField(k, autoRestoreUndo.previous[k]);
              toast.info(`Undid auto-restore for ${autoRestoreUndo.keys.length} field${autoRestoreUndo.keys.length === 1 ? "" : "s"}.`);
              setAutoRestoreUndo(null);
            }}
            data-testid="pd-auto-restore-undo-btn">
            Undo restore
          </button>
          <style>{`
            @keyframes pd-undo-countdown {
              from { stroke-dashoffset: 0; }
              to   { stroke-dashoffset: 50.265; }
            }
          `}</style>
        </div>
      )}
      {/* Iter98f · Auto-save recovery banner — one-click restore of
          fields stashed from a previous project that got deleted.
          Iter98g · Now expands to a preview so users see exactly
          which field values will land in the current project. */}
      {recoveryOffer && (
        <div className="shrink-0 bg-amber-50 border-b border-amber-200"
                 data-testid="pd-recovery-banner">
          <div className="px-6 py-3 flex items-center gap-3 text-[12.5px]">
            <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
            <div className="flex-1">
              <b className="text-amber-900">Unsaved changes recovered</b>{" "}
              <span className="text-amber-800">
                from <i>{recoveryOffer.project_name || recoveryOffer.project_id?.slice(0, 8)}</i>{" "}
                — {Object.keys(recoveryOffer.patch || {}).length} field(s) stashed{" "}
                {recoveryOffer.stashed_at ? "at " + String(recoveryOffer.stashed_at).slice(0, 19).replace("T", " ") : ""}.
              </span>
            </div>
            <button
              type="button"
              className="text-[12px] font-medium text-amber-900 hover:text-amber-950 underline"
              onClick={() => setRecoveryOfferOpen((v) => !v)}
              data-testid="pd-recovery-preview">
              {recoveryOfferOpen ? "Hide preview" : "Preview"}
            </button>
            <button
              type="button"
              className="text-[12px] font-medium text-amber-900 hover:text-amber-950 underline"
              onClick={() => {
                const patch = recoveryOffer.patch || {};
                const keys = Object.keys(patch);
                for (const k of keys) patchField(k, patch[k]);
                window.localStorage.removeItem("sres.recovery.orphan");
                setRecoveryOffer(null);
                toast.success(`Restored ${keys.length} field${keys.length === 1 ? "" : "s"} into this project.`);
              }}
              data-testid="pd-recovery-restore">
              Restore
            </button>
            <button
              type="button"
              className="text-[12px] text-amber-700 hover:text-amber-900"
              onClick={() => {
                window.localStorage.removeItem("sres.recovery.orphan");
                setRecoveryOffer(null);
              }}
              data-testid="pd-recovery-discard">
              Discard
            </button>
          </div>
          {recoveryOfferOpen && (
            <div className="px-6 pb-3 pt-1" data-testid="pd-recovery-preview-panel">
              <div className="rounded-md border border-amber-300 bg-white/70 p-2 max-h-40 overflow-y-auto">
                <div className="table-wrapper">
                  <table className="w-full text-[11.5px]">
                    <thead className="text-amber-900">
                      <tr>
                        <th className="text-left font-semibold pr-3 py-1 uppercase tracking-widest text-[10px]">Field</th>
                        <th className="text-left font-semibold pr-3 py-1 uppercase tracking-widest text-[10px]">Stashed value</th>
                        <th className="text-left font-semibold py-1 uppercase tracking-widest text-[10px]">Current value</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-700">
                      {Object.entries(recoveryOffer.patch || {}).map(([k, v]) => (
                        <tr key={k} className="border-t border-amber-200"
                                data-testid={`pd-recovery-row-${k}`}>
                          <td className="pr-3 py-1 font-mono text-[11px] text-slate-600">{k}</td>
                          <td className="pr-3 py-1 font-medium text-slate-900 truncate max-w-[280px]"
                                  title={String(v ?? "")}>
                            {String(v ?? "") || <i className="text-slate-400">empty</i>}
                          </td>
                          <td className="py-1 text-slate-500 truncate max-w-[280px]"
                                  title={String(form[k] ?? "")}>
                            {String(form[k] ?? "") || <i className="text-slate-400">empty</i>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* ───────── Header ───────── */}
      <header className="shrink-0 bg-white border-b border-slate-200"
                     data-testid="pd-header">
        <div className="px-6 py-3 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">
              Stage 2 of 9 · Project Details
            </div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900 leading-tight"
                    data-testid="pd-title">
              Project Details
            </h1>
            <div className="text-[11px] text-slate-500 mt-0.5 truncate">
              {form.name || "Untitled project"} · {form.commodity || "—"} · {form.reporting_standard.replace(/_/g, " ")}
            </div>
          </div>
          <div className="text-[10.5px] uppercase tracking-widest flex items-center gap-1.5"
                  data-testid="pd-save-state" data-save-state={saveState}>
            {saveState === "saved" ? (
              <CheckCircle size={12} className="text-emerald-600" />
            ) : (
              <CloudArrowUp size={12} className={saveState === "error" ? "text-rose-600" : "text-amber-600"} />
            )}
            <span className={saveState === "saved" ? "text-emerald-700"
                              : saveState === "error" ? "text-rose-700"
                              : "text-amber-700"}>
              {saveLabel}
            </span>
          </div>
        </div>
      </header>

      {/* ───────── Body ───────── */}
      <main className="flex-1 min-h-0 overflow-y-auto" data-testid="pd-scroll">
        <div className="max-w-4xl mx-auto px-6 py-6 space-y-5 pb-24">

          {/* Iter93 · Feb 2026 · Archive audit banner. Renders only when
              the project is currently archived, giving the reviewer the
              who/when/why at a glance for accountability.
              Iter94 · Feb 2026 · now includes a scrollable timeline of
              every archive/restore cycle sourced from
              /api/projects/{pid}/archive-events. */}
          {form.archived && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3
                                 flex flex-col gap-2"
                      data-testid="pd-archive-audit-banner">
              <div className="flex items-start gap-3">
                <div className="text-[18px] leading-none pt-0.5">📦</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-heading font-black tracking-wider uppercase text-amber-900">
                    Archived project · read-only shelf
                  </div>
                  <div className="text-[11.5px] text-amber-800 mt-0.5 font-mono">
                    {form.archive_reason && <span className="uppercase font-bold">Reason: {form.archive_reason} · </span>}
                    {form.archived_by ? `by ${form.archived_by}` : "actor unknown"}
                    {form.archived_at ? ` · ${new Date(form.archived_at).toLocaleString()}` : ""}
                  </div>
                  {form.archive_note && (
                    <div className="text-[11px] text-amber-700 mt-1 italic"
                              data-testid="pd-archive-note">
                      “{form.archive_note}”
                    </div>
                  )}
                </div>
              </div>
              <ArchiveTimeline projectId={pid} />
            </div>
          )}

          {/* Section A — Project Identity */}
          <Section title="Project Identity" testId="pd-section-identity">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Project Name" required
                value={form.name}
                onChange={(v) => patchField("name", v)}
                testId="pd-field-name" />
              <TextField label="Project Number"
                value={form.project_code}
                onChange={(v) => patchField("project_code", v)}
                testId="pd-field-project-number" />
              <SelectField label="Report Type"
                value={form.report_type}
                options={REPORT_TYPES}
                onChange={(v) => patchField("report_type", v)}
                testId="pd-field-report-type" />
              <SelectField label="Reporting Standard"
                value={form.reporting_standard}
                options={REPORTING_STANDARDS}
                onChange={(v) => patchField("reporting_standard", v)}
                testId="pd-field-reporting-standard" />
              <TextField label="Report Number"
                value={form.report_number}
                onChange={(v) => patchField("report_number", v)}
                hint="Auto-generated on first save · editable"
                testId="pd-field-report-number" />
            </div>
          </Section>

          {/* Section B — Client / Owner */}
          <Section title="Client / Owner" testId="pd-section-client">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Company Name" required
                value={form.client}
                onChange={(v) => patchField("client", v)}
                testId="pd-field-client-name" />
              <TextField label="Address"
                value={form.client_address}
                onChange={(v) => patchField("client_address", v)}
                testId="pd-field-client-address" />
              <TextField label="Email" type="email"
                value={form.client_email}
                onChange={(v) => patchField("client_email", v)}
                testId="pd-field-client-email" />
              <TextField label="Contact Number" type="tel"
                value={form.client_contact}
                onChange={(v) => patchField("client_contact", v)}
                testId="pd-field-client-contact" />
            </div>
          </Section>

          {/* Section C — Consultant / Prepared By */}
          <Section title="Consultant / Prepared By" testId="pd-section-consultant">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Company Name"
                value={form.consultant}
                onChange={(v) => patchField("consultant", v)}
                testId="pd-field-consultant-name" />
              <TextField label="Address"
                value={form.consultant_address}
                onChange={(v) => patchField("consultant_address", v)}
                testId="pd-field-consultant-address" />
              <TextField label="Email" type="email"
                value={form.consultant_email}
                onChange={(v) => patchField("consultant_email", v)}
                testId="pd-field-consultant-email" />
              <TextField label="Contact Number" type="tel"
                value={form.consultant_contact}
                onChange={(v) => patchField("consultant_contact", v)}
                testId="pd-field-consultant-contact" />
            </div>
          </Section>

          {/* Section D — Technical */}
          <Section title="Technical" testId="pd-section-technical">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="Commodity" required
                value={form.commodity}
                options={COMMODITIES}
                onChange={(v) => patchField("commodity", v)}
                testId="pd-field-commodity" />
              <SelectField label="Grade Unit"
                value={form.grade_unit}
                options={GRADE_UNITS}
                onChange={(v) => patchField("grade_unit", v)}
                testId="pd-field-grade-unit" />
              <SelectField label="Currency"
                value={form.reporting_currency}
                options={CURRENCIES}
                onChange={(v) => patchField("reporting_currency", v)}
                testId="pd-field-currency" />
              <SelectField label="Country"
                value={form.country}
                options={(countriesQ.data?.items || []).map((c) => [c.name, c.name])}
                onChange={handleCountryChange}
                testId="pd-field-country"
                placeholder={countriesQ.isLoading ? "Loading…" : "Select a country"} />
              <TextField label="Datum"
                value={form.datum}
                onChange={(v) => patchField("datum", v)}
                hint="Auto-filled from country · editable"
                testId="pd-field-datum" />
              <TextField label="UTM Zone"
                value={form.utm_zone}
                onChange={(v) => patchField("utm_zone", v)}
                hint="Auto-filled from country · finalised after boundary upload"
                testId="pd-field-utm-zone" />
              <TextField label="EPSG"
                value={form.epsg}
                onChange={(v) => patchField("epsg", v)}
                hint="Auto-filled from country · editable"
                testId="pd-field-epsg" />
              <TextField label="Coordinate System"
                value={form.coordinate_system}
                onChange={(v) => patchField("coordinate_system", v)}
                hint="Auto-filled · editable"
                testId="pd-field-coordinate-system" />
            </div>

            {/* Auto-fill preview banner */}
            {autoFillMeta && (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3"
                        data-testid="pd-crs-autofill-banner">
                <div className="flex items-start gap-2">
                  <Compass size={16} className="text-emerald-700 mt-0.5" weight="duotone" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-heading font-bold text-emerald-900 uppercase tracking-widest">
                      CRS auto-fill applied
                    </div>
                    <div className="mt-1 grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
                      <BannerField label="Country" value={autoFillMeta.country} testId="pd-banner-country" />
                      <BannerField label="Datum"   value={autoFillMeta.datum}     testId="pd-banner-datum" mono />
                      <BannerField label="UTM Zone" value={autoFillMeta.utm_zone || "—"} testId="pd-banner-utm" mono />
                      <BannerField label="EPSG"    value={autoFillMeta.epsg || "—"} testId="pd-banner-epsg" mono />
                      <BannerField label="Confidence"
                        value={`${Math.round((autoFillMeta.confidence || 0) * 100)}%`}
                        testId="pd-banner-confidence" mono />
                    </div>
                    {autoFillMeta.multi_zone_hint && (
                      <div className="mt-2 text-[10.5px] text-emerald-800 italic"
                                data-testid="pd-banner-multi-zone-hint">
                        This country spans multiple UTM zones. The final zone will be
                        confirmed automatically when you upload the boundary file.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Overwrite confirmation */}
            {pendingCountry && (
              <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3"
                        data-testid="pd-crs-overwrite-confirm">
                <div className="text-[12px] font-heading font-bold text-amber-900 uppercase tracking-widest">
                  Overwrite manual edits?
                </div>
                <div className="mt-1 text-[11px] text-amber-800">
                  Selecting <b>{pendingCountry.country}</b> would replace these values
                  you edited manually:
                </div>
                <ul className="mt-2 text-[11px] text-amber-900 font-mono space-y-0.5">
                  {pendingCountry.overwrites.map((o) => (
                    <li key={o.field}>
                      • <b>{o.field}</b>: &quot;{String(o.prev)}&quot; → &quot;{String(o.next)}&quot;
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={cancelOverwrite}
                              data-testid="pd-overwrite-cancel"
                              className="text-[11px]">
                    Keep my edits
                  </Button>
                  <Button size="sm" onClick={confirmOverwrite}
                              data-testid="pd-overwrite-confirm"
                              className="bg-amber-700 hover:bg-amber-800 text-white text-[11px]">
                    Overwrite with defaults
                  </Button>
                </div>
              </div>
            )}
          </Section>
        </div>
      </main>

      {/* ───────── Footer — Back · Save & Continue ───────── */}
      <footer
        className="shrink-0 border-t border-slate-200 bg-white px-6 py-3"
        data-testid="pd-footer"
      >
        <div className="max-w-4xl mx-auto flex items-center gap-2 flex-wrap">
          <Button
            type="button" size="sm" variant="ghost"
            onClick={handleReturnDashboard}
            data-testid="flow-return-dashboard"
            className="text-slate-500 hover:text-brand-blue text-[11.5px] gap-1"
          >
            <House size={12} /> Dashboard
          </Button>
          <div className="flex-1" />
          <Button
            type="button" size="sm" variant="outline"
            onClick={handleBack}
            data-testid="flow-back"
            className="text-[11.5px] gap-1"
          >
            <ArrowLeft size={12} /> Back
          </Button>
          <Button
            type="button" size="sm" variant="outline"
            onClick={handleSave}
            disabled={saveState === "saving"}
            data-testid="flow-save"
            className="text-[11.5px] gap-1"
          >
            <CloudArrowUp size={12} /> {saveState === "saving" ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button" size="sm"
            onClick={handleSaveContinue}
            disabled={saveState === "saving"}
            data-testid="flow-save-continue"
            className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11.5px] gap-1"
          >
            {saveState === "saving" ? "Saving…" : "Save & Continue"}
            <ArrowRight size={12} weight="bold" />
          </Button>
        </div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Presentational atoms — kept in-file so the page stands alone.
// ─────────────────────────────────────────────────────────────────
function Section({ title, testId, children }) {
  return (
    <section
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid={testId}
    >
      <div className="px-4 py-2.5 border-b border-slate-100">
        <h2 className="font-heading font-bold text-[14px] tracking-tight text-slate-900">
          {title}
        </h2>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function TextField({ label, value, onChange, type = "text", required, readOnly, hint, testId }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </div>
      <input
        type={type}
        value={value ?? ""}
        readOnly={!!readOnly}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={testId}
        className={`w-full rounded-md border ${readOnly ? "bg-slate-50 border-slate-200 text-slate-600" : "bg-white border-slate-200 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue/30"} px-3 py-2 text-[13px] text-slate-800 focus:outline-none transition-all`}
      />
      {hint && <div className="mt-1 text-[10.5px] text-slate-400">{hint}</div>}
    </label>
  );
}

function SelectField({ label, value, options, onChange, required, testId, placeholder }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </div>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="h-10 text-[13px]">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => (
            <SelectItem key={v} value={v}>{l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

function BannerField({ label, value, mono, testId }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-widest text-emerald-700 opacity-80">
        {label}
      </div>
      <div className={`text-[12.5px] font-bold text-emerald-900 truncate ${mono ? "font-mono" : ""}`}
              data-testid={testId} title={String(value ?? "")}>
        {value ?? "—"}
      </div>
    </div>
  );
}
